import Runtime       from "./Runtime"
import InputHandler  from "./InputHandler"
import MapLoader     from "./MapLoader"
import TravelerMoved from "./changes/TravelerMoved"
import InitialState  from "./changes/InitialState"
import ResumeGame    from "./changes/ResumeGame"
import LoadingMap    from "./changes/LoadingMap"
import LoadedMap     from "./changes/LoadedMap"
import LoadUrl       from "./changes/LoadUrl"

/* Represents the current state of the game
 * itself.  Where is the traveler, on what map are
 * they traveling, etc etc.
 */
class Game {
  constructor({ window, gameMap, travelerStartX, travelerStartY, newGame, log, mapLoader}) {
    this.window               = window
    this.gameMap              = gameMap
    this.travelerX            = travelerStartX
    this.travelerY            = travelerStartY
    this.log                  = log.forClass("Game")
    this.mapLoader            = mapLoader
    this.inputHandler         = null
    this.stateChangeListeners = []
    this.newGame              = newGame
    this.inputHandler         = new InputHandler({
      document: this.window.document,
      onLeft: this.travelerLeft.bind(this),
      onRight: this.travelerRight.bind(this),
      onUp: this.travelerUp.bind(this),
      onDown: this.travelerDown.bind(this),
      log: this.log
    })
  }

  addStateChangeListener(listener) {
    this.stateChangeListeners.push(listener)
  }

  start(reloadFromError) {
    const logger = this.log.method("start")
    const devNotification = Runtime.env().isDev() ? "[ This is Dev ]" : undefined
    const errorNotification = reloadFromError ? " (Looks like some error happened and the page was reloaded)" : undefined
    if (this.newGame) {
      const message = [
        "Welcome!",
        devNotification,
        errorNotification,
      ].filter( (m) => m ).join(" ")
      this.fireStateChange(new InitialState(message))
    }
    else {
      const message = [
        "Welcome Back!",
        devNotification,
        errorNotification,
      ].filter( (m) => m ).join(" ")
      this.fireStateChange(new ResumeGame(message))
    }
    logger.end()
  }

  travelerLeft() {
    let newX = this.travelerX - 1
    if (newX < 0) {
      newX = this.gameMap.width - 1
    }
    return this._move(newX, this.travelerY)
  }

  travelerRight() {
    let newX = this.travelerX + 1
    if (newX >= this.gameMap.width) {
      newX = 0
    }
    return this._move(newX, this.travelerY)
  }

  travelerUp() {
    let newY = this.travelerY - 1
    if (newY < 0) {
      newY = this.gameMap.height - 1
    }
    return this._move(this.travelerX, newY)
  }

  travelerDown() {
    let newY = this.travelerY + 1
    if (newY >= this.gameMap.height) {
      newY = 0
    }
    return this._move(this.travelerX, newY)
  }

  fireStateChange(change) {
    this.stateChangeListeners.forEach( (listener) => {
      listener(this,change)
    })
  }

  _checkTraversable(change) {
    const logger = this.log.method("_checkTraversable", { change: change })
    const terrainTileHeight = this.gameMap.terrainTileAt(change.newX, change.newY).height
    if ( (terrainTileHeight >= this.gameMap.traveler.travel_min) && (terrainTileHeight <= this.gameMap.traveler.travel_max) ) {
     
      logger.addDetails({ canMove: true })
    } else {
      logger.addDetails({ canMove: false })
      change.notMoved()
    }
    logger.end()
    return change
  }

  _checkPortal(change) {
    const logger = this.log.method("_checkPortal", { change: change })
    const portal = this.gameMap.portalAt(change.newX, change.newY)
    if (portal) {
      logger.addDetails({ portal: true })
      if (portal.destination_coordinates) {
        logger.addDetails({ local: true, destination: portal.destination_coordinates })
        change.setCoordinates(portal.destination_coordinates)
        change.setMoved()
        if (portal.new_traveler_name) {
          logger.addDetails({ newTraveler: portal.new_traveler_name })
          this.gameMap.changeTraveler(portal.new_traveler_name)
        }
      }
      else if (portal.url) {
        logger.end({ loadingUrl: portal.url })
        return new LoadUrl(portal.url, portal.end_of_game)
      }
      else {
        logger.end({ local: false, destination: portal.destination.map })
        this.fireStateChange(new LoadingMap())
        return this.mapLoader.loadMap({
          mapName: portal.destination.map
        }).then( (newGameMap)  => {
          this.fireStateChange(new LoadedMap())
          this.gameMap = newGameMap
          if (portal.new_traveler_name) {
            logger.addDetails({ newTraveler: portal.new_traveler_name })
            this.gameMap.changeTraveler(portal.new_traveler_name)
          }
          if (portal.destination.coordinates) {
            change.setCoordinates(portal.destination.coordinates)
          }
          else if (portal.destination.location) {
            change.setCoordinates(newGameMap.coordinatesForLocation(portal.destination.location))
          }
          else {
            throw `Portal ${portal}'s destintation should have either coordinates or a location`
          }
          change.setMoved()
          change.changeTraveler(portal.new_traveler_name)
          return change
        })
      }
    }
    else {
      logger.addDetails({ portal: false })
    }
    logger.end()
    return change
  }

  _checkMessage(change) {
    const logger = this.log.method("_checkMessage", { change: change })
    const message = this.gameMap.messageAt(change.newX,change.newY)
    if (message) {
      logger.addDetails({ message: true })
      change.setMessage(message)
      change.notMoved()
    }
    else {
      logger.addDetails({ message: false })
    }
    logger.end()
    return change
  }

  _checkCharacter(change) {
    const logger = this.log.method("_checkCharacter", { change: change })
    const character = this.gameMap.characterAt(change.newX,change.newY)
    if (character) {
      logger.addDetails({ character: true })
      change.setCharacter(character)
      change.notMoved()
    }
    else {
      logger.addDetails({ character: false })
    }
    logger.end()
    return change
  }

  _move(newX, newY) {
    new Promise( (resolve, reject) => {
      resolve(new TravelerMoved({newX: newX, newY: newY}))
    }).then(
      this._checkTraversable.bind(this)
    ).then(
      this._checkPortal.bind(this)
    ).then(
      this._checkMessage.bind(this)
    ).then(
      this._checkCharacter.bind(this)
    ).then( (change) => {
      if (change.isMoved()) {
        change.saveOldCoordinates([ this.travelerX, this.travelerY ])
        this.travelerX = change.newX
        this.travelerY = change.newY
      }
      this.fireStateChange(change)
    })
  }


}

export default Game
